import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChatCircleDots,
  Crown,
  ArrowRight,
  Bell,
  TrashSimple,
  CalendarBlank,
  Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/razorpay";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "chats";
  const [tab, setTab] = useState(initialTab);
  const [convs, setConvs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    api
      .get("/conversations")
      .then((r) => setConvs(r.data.conversations || []))
      .catch(() => {});
    api
      .get("/price-alerts")
      .then((r) => setAlerts(r.data.alerts || []))
      .catch(() => {});
    api
      .get("/billing/subscription")
      .then((r) => setSub(r.data))
      .catch(() => {});
  }, [user, loading, navigate]);

  const refreshSub = () =>
    api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => {});

  const onUpgrade = async (planCode) => {
    if (!sub?.razorpay_enabled) {
      toast.info("Live payments aren't enabled yet — head to /pricing to reserve your spot.");
      navigate("/pricing");
      return;
    }
    setBusy(planCode);
    try {
      const result = await startCheckout(planCode, {
        name: user.name,
        email: user.email,
        contact: user.phone,
      });
      toast.success(`Plan active until ${new Date(result.plan_active_until).toLocaleDateString()}`);
      await refreshSub();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Payment failed";
      if (msg !== "Payment cancelled") toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const toggleAlert = async (id, isActive) => {
    try {
      await api.patch(`/price-alerts/${id}?is_active=${!isActive}`);
      setAlerts((a) =>
        a.map((x) => (x.id === id ? { ...x, is_active: !isActive } : x)),
      );
    } catch {
      toast.error("Could not update");
    }
  };

  const deleteAlert = async (id) => {
    try {
      await api.delete(`/price-alerts/${id}`);
      setAlerts((a) => a.filter((x) => x.id !== id));
      toast.success("Alert removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  if (loading || !user) return null;

  return (
    <div data-testid="dashboard-page">
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
          Dashboard
        </p>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0A1128]">
          Vanakkam, {user.name?.split(" ")[0]}!
        </h1>

        <div className="mt-8 flex gap-1 border-b border-[#E8DFD1]" data-testid="dashboard-tabs">
          {[
            { k: "chats", label: "My Chats", count: convs.length },
            { k: "alerts", label: "Price Alerts", count: alerts.length },
            { k: "plan", label: "My Plan" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 sm:px-5 py-3 text-sm smooth border-b-2 -mb-px ${
                tab === t.k
                  ? "border-[#3B5BDB] text-[#3B5BDB] font-medium"
                  : "border-transparent text-[#4A5568] hover:text-[#0A1128]"
              }`}
            >
              {t.label}
              {t.count !== undefined && (
                <span className="ml-1.5 text-xs opacity-70">({t.count})</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "chats" && (
            <div className="mc-card p-6 sm:p-8" data-testid="my-chats-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ChatCircleDots size={20} weight="duotone" className="text-[#3B5BDB]" />
                  <h2 className="font-display text-2xl text-[#0A1128]">My chats</h2>
                </div>
                <Link
                  to="/chat"
                  className="text-sm text-[#3B5BDB] flex items-center gap-1 hover:gap-2 smooth"
                  data-testid="new-chat-link"
                >
                  New chat <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
              {convs.length === 0 ? (
                <div className="mt-6 text-center py-10">
                  <p className="text-[#4A5568]">
                    You haven't asked Mine AI anything yet.
                  </p>
                  <Link
                    to="/chat"
                    className="inline-block mt-3 px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth"
                  >
                    Start your first chat
                  </Link>
                </div>
              ) : (
                <ul className="mt-5 divide-y divide-[#E8DFD1]">
                  {convs.map((c) => (
                    <li
                      key={c.id}
                      className="py-3 flex items-center justify-between"
                      data-testid={`conv-${c.id}`}
                    >
                      <div>
                        <p className="text-sm text-[#0A1128]">{c.title}</p>
                        <p className="text-xs text-[#4A5568]">
                          {new Date(c.created_at).toLocaleString()}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "alerts" && (
            <div className="mc-card p-6 sm:p-8" data-testid="my-alerts-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={20} weight="duotone" className="text-[#3B5BDB]" />
                  <h2 className="font-display text-2xl text-[#0A1128]">Price alerts</h2>
                </div>
                <Link
                  to="/price-check"
                  className="text-sm text-[#3B5BDB] flex items-center gap-1 hover:gap-2 smooth"
                >
                  Set new alert <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
              {alerts.length === 0 ? (
                <div className="mt-6 text-center py-10">
                  <p className="text-[#4A5568]">
                    No alerts yet. Set one on the Price Check page.
                  </p>
                </div>
              ) : (
                <ul className="mt-5 divide-y divide-[#E8DFD1]">
                  {alerts.map((a) => (
                    <li
                      key={a.id}
                      className="py-4 flex items-center justify-between gap-3"
                      data-testid={`alert-${a.id}`}
                    >
                      <div>
                        <p className="text-sm text-[#0A1128]">
                          <span className="font-medium capitalize">{a.metal}</span> {a.alert_type}{" "}
                          <span className="font-display text-base text-[#3B5BDB]">
                            ₹{Number(a.target_price).toLocaleString("en-IN")}
                          </span>
                          /g · <span className="capitalize">{a.city}</span>
                        </p>
                        <p className="text-xs text-[#4A5568]">
                          to {a.email} · {new Date(a.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`alert-toggle-${a.id}`}
                          onClick={() => toggleAlert(a.id, a.is_active)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium smooth ${
                            a.is_active
                              ? "bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/30"
                              : "bg-[#F5EFE6] text-[#4A5568] border border-[#E8DFD1]"
                          }`}
                        >
                          {a.is_active ? "Active" : "Paused"}
                        </button>
                        <button
                          data-testid={`alert-delete-${a.id}`}
                          onClick={() => deleteAlert(a.id)}
                          className="p-2 text-[#4A5568] hover:text-[#DC2626] smooth"
                        >
                          <TrashSimple size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {tab === "plan" && (
            <div className="mc-card p-6 sm:p-8" data-testid="my-plan-card">
              <div className="flex items-center gap-2">
                <Crown size={20} weight="duotone" className="text-[#D4AF37]" />
                <h2 className="font-display text-2xl text-[#0A1128]">My plan</h2>
              </div>

              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#4A5568]">
                    Current plan
                  </p>
                  <p
                    className="font-display text-3xl mt-1 text-[#3B5BDB]"
                    data-testid="plan-current-name"
                  >
                    {(sub?.plan || user.plan || "free").toUpperCase()}
                  </p>
                  {sub?.plan_active_until ? (
                    <p
                      className="mt-2 text-sm text-[#4A5568] flex items-center gap-1.5"
                      data-testid="plan-active-until"
                    >
                      <CalendarBlank size={14} weight="duotone" className="text-[#3B5BDB]" />
                      Active until{" "}
                      <span className="font-medium text-[#0A1128]">
                        {new Date(sub.plan_active_until).toLocaleDateString()}
                      </span>{" "}
                      · {sub.days_left} days left
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-[#4A5568]">
                      You're on the free plan. Upgrade to unlock unlimited chats, Style Match, Resell Valuator and price alerts.
                    </p>
                  )}
                </div>
                <div className="sm:text-right">
                  {(sub?.plan || user.plan) !== "pro" && (
                    <button
                      data-testid="upgrade-plus-btn"
                      onClick={() => onUpgrade("plus")}
                      disabled={busy === "plus"}
                      className="w-full sm:w-auto px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
                    >
                      {busy === "plus"
                        ? "Opening checkout..."
                        : (sub?.plan || user.plan) === "plus"
                          ? "Renew Plus · ₹199"
                          : "Upgrade to Plus · ₹199/mo"}
                    </button>
                  )}
                  <button
                    data-testid="upgrade-pro-btn"
                    onClick={() => onUpgrade("pro")}
                    disabled={busy === "pro"}
                    className="mt-2 w-full sm:w-auto sm:ml-2 px-5 py-2.5 rounded-md bg-[#0A1128] text-white font-medium hover:bg-[#0A1128]/90 smooth disabled:opacity-60"
                  >
                    {busy === "pro"
                      ? "Opening checkout..."
                      : (sub?.plan || user.plan) === "pro"
                        ? "Renew Pro · ₹499"
                        : "Upgrade to Pro · ₹499/mo"}
                  </button>
                </div>
              </div>

              {sub?.history?.length > 0 && (
                <div className="mt-7 pt-5 border-t border-[#E8DFD1]">
                  <p className="text-xs uppercase tracking-wider text-[#4A5568] flex items-center gap-1.5">
                    <Receipt size={12} weight="duotone" /> Payment history
                  </p>
                  <ul className="mt-3 divide-y divide-[#E8DFD1]" data-testid="plan-history">
                    {sub.history.map((h) => (
                      <li
                        key={h.razorpay_payment_id || h.razorpay_order_id}
                        className="py-2.5 flex items-center justify-between text-sm"
                      >
                        <span className="capitalize text-[#0A1128]">
                          {h.plan} · ₹{(h.amount_paise / 100).toLocaleString("en-IN")}
                        </span>
                        <span className="text-xs text-[#4A5568] tabular-nums">
                          {new Date(h.created_at).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 text-xs text-[#4A5568] space-y-0.5">
                <p>Email: {user.email}</p>
                <p>Phone: {user.phone}</p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
